import { useEffect, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Truck, Trash, Plus, ArrowClockwise, CheckCircle, Warning } from "@phosphor-icons/react";

const CATEGORIES = ["plumbing", "electrical", "hvac", "hardware", "paint", "cleaning", "safety", "tools", "other"];
const UNITS = ["each", "box", "pack", "roll", "tube", "gallon", "quart", "pair", "set", "ft"];

const catColor = (c) => ({
  plumbing: "bg-cyan-100 text-cyan-800 border-cyan-500",
  electrical: "bg-yellow-100 text-yellow-800 border-yellow-500",
  hvac: "bg-orange-100 text-orange-800 border-orange-500",
  hardware: "bg-slate-100 text-slate-800 border-slate-500",
  paint: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-500",
  cleaning: "bg-emerald-100 text-emerald-800 border-emerald-500",
  safety: "bg-red-100 text-red-800 border-red-500",
  tools: "bg-amber-100 text-amber-800 border-amber-500",
}[c] || "bg-slate-100 text-slate-700 border-slate-400");

const confColor = (c) => ({
  high: "bg-emerald-600 text-white",
  medium: "bg-amber-500 text-white",
  low: "bg-red-600 text-white",
}[c] || "bg-slate-500 text-white");

export default function TruckStock() {
  const [stock, setStock] = useState([]);
  const [updatedAt, setUpdatedAt] = useState("");
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [proposed, setProposed] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/truck-stock");
      setStock(r.data?.items || []);
      setUpdatedAt(r.data?.updated_at || "");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load truck stock");
    }
  };
  useEffect(() => { load(); }, []);

  const doScan = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setScanning(true);
    const tid = toast.loading(`Scanning ${fileList.length} photo${fileList.length === 1 ? "" : "s"}…`);
    try {
      const fd = new FormData();
      for (const f of fileList) fd.append("files", f);
      const r = await api.post("/truck-stock/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.dismiss(tid);
      const items = r.data?.items || [];
      if (!items.length) {
        toast.error("AI didn't find any inventory in those photos — try better lighting or closer shots");
      } else {
        toast.success(`Detected ${items.length} item${items.length === 1 ? "" : "s"} · review + confirm`);
        setProposed(items);
      }
    } catch (e) {
      toast.dismiss(tid);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Scan failed");
    } finally { setScanning(false); }
  };

  const acceptProposed = () => {
    // Merge into existing stock — replace matches by name, add rest
    const byName = new Map(stock.map(s => [s.name.toLowerCase(), s]));
    for (const p of proposed) {
      byName.set(p.name.toLowerCase(), p);
    }
    setStock([...byName.values()]);
    setProposed(null);
    toast.success("Merged into truck stock — hit Save to commit");
  };

  const replaceStock = () => {
    setStock(proposed);
    setProposed(null);
    toast.success("Replaced current stock with new scan — hit Save to commit");
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post("/truck-stock", { items: stock, scan_photos: [] });
      toast.success(`Saved · ${r.data.item_count} items on your truck`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const updateRow = (i, field, value) => {
    setStock(prev => prev.map((it, idx) => idx === i ? { ...it, [field]: value } : it));
  };
  const deleteRow = (i) => setStock(prev => prev.filter((_, idx) => idx !== i));
  const addRow = () => setStock(prev => [...prev, { name: "", category: "other", qty: 1, unit: "each", notes: "", confidence: "high" }]);

  const totalUnits = stock.reduce((s, i) => s + (parseInt(i.qty) || 0), 0);

  return (
    <AdminShell>
      <div className="max-w-5xl mx-auto p-4 space-y-4" data-testid="truck-stock-page">
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-amber-700">Field Mode</div>
          <h1 className="font-display font-black text-3xl uppercase tracking-tight flex items-center gap-2">
            <Truck weight="fill" size={28}/> Truck Stock
          </h1>
          <div className="text-xs text-slate-500 mt-1">
            Snap a photo of your van shelves. AI reads what's on them so you know before you roll to a job.
          </div>
        </div>

        {/* Scan action */}
        <div className="p-4 border-4 border-[#0f172a] bg-amber-50 shadow-[6px_6px_0_#0f172a]">
          <div className="flex items-start gap-3 flex-wrap">
            <label className={`h-14 px-4 border-2 border-[#0f172a] bg-amber-400 hover:bg-amber-300 text-slate-900 font-display font-black uppercase text-sm tracking-widest inline-flex items-center gap-2 cursor-pointer shadow-[3px_3px_0_#0f172a] transition-transform hover:-translate-y-0.5 ${scanning ? "opacity-60 cursor-wait" : ""}`}
              data-testid="truck-scan-btn">
              <Camera weight="fill" size={20}/> {scanning ? "SCANNING…" : "SCAN MY TRUCK"}
              <input type="file" accept="image/*" multiple hidden disabled={scanning}
                onChange={e => { doScan(Array.from(e.target.files || [])); e.target.value = ""; }}/>
            </label>
            <div className="flex-1 min-w-[200px]">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-600">On your truck right now</div>
              <div className="font-display font-black text-2xl">{totalUnits} <span className="text-sm text-slate-500 font-mono">units · {stock.length} lines</span></div>
              {updatedAt && <div className="text-[10px] text-slate-500">Updated {new Date(updatedAt).toLocaleString()}</div>}
            </div>
            <button onClick={save} disabled={saving}
              data-testid="truck-save-btn"
              className="h-14 px-6 border-2 border-[#0f172a] bg-emerald-500 hover:bg-emerald-400 text-white font-black uppercase text-sm tracking-widest inline-flex items-center gap-2 shadow-[3px_3px_0_#0f172a]">
              <CheckCircle weight="fill" size={20}/> {saving ? "SAVING…" : "SAVE STOCK"}
            </button>
          </div>
          <div className="mt-3 text-[11px] text-slate-600 italic">
            Tip: 2-3 well-lit shots beat 6 blurry ones. Get close on shelves with small parts.
          </div>
        </div>

        {/* Proposed items from scan */}
        {proposed && (
          <div className="p-4 border-4 border-cyan-600 bg-cyan-50 shadow-[6px_6px_0_#0891b2]"
            data-testid="proposed-panel">
            <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-cyan-800">Detected in your photos</div>
                <div className="font-display font-black text-xl">{proposed.length} items found</div>
                <div className="text-[11px] text-slate-600 italic">Review — anything amber/red confidence needs a check.</div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={acceptProposed} data-testid="merge-proposed"
                  className="h-10 px-3 border-2 border-emerald-700 bg-emerald-600 hover:bg-emerald-500 text-white font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1">
                  <Plus weight="bold" size={14}/> Merge into stock
                </button>
                <button onClick={replaceStock} data-testid="replace-proposed"
                  className="h-10 px-3 border-2 border-red-700 bg-red-600 hover:bg-red-500 text-white font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1">
                  <ArrowClockwise weight="bold" size={14}/> Replace all
                </button>
                <button onClick={() => setProposed(null)}
                  className="h-10 px-3 border-2 border-slate-500 bg-white hover:bg-slate-100 font-black uppercase text-[11px] tracking-widest">
                  Cancel
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {proposed.map((p, i) => (
                <div key={i} className="p-2 bg-white border-2 border-cyan-300">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-sm">{p.name}</div>
                      <div className="mt-0.5 flex items-center gap-1 flex-wrap">
                        <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest border ${catColor(p.category)}`}>{p.category}</span>
                        <span className="text-xs font-mono">{p.qty} {p.unit}</span>
                        <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ${confColor(p.confidence)}`}>{p.confidence}</span>
                      </div>
                      {p.notes && <div className="text-[10px] text-slate-500 italic mt-0.5">{p.notes}</div>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Current stock table */}
        <div className="bg-white border-2 border-slate-300">
          <div className="p-3 flex items-center justify-between border-b-2 border-slate-200">
            <div className="font-black uppercase text-xs tracking-widest">Current Stock</div>
            <button onClick={addRow} data-testid="add-row-btn"
              className="h-8 px-3 border-2 border-slate-900 bg-white hover:bg-slate-100 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
              <Plus weight="bold" size={12}/> Add item
            </button>
          </div>
          {stock.length === 0 ? (
            <div className="p-6 text-center text-slate-500 text-sm">
              <Warning size={24} className="mx-auto text-slate-400"/>
              <div className="mt-2 font-black uppercase text-xs tracking-widest">Empty truck</div>
              <div className="text-[11px] mt-1">Tap <b>SCAN MY TRUCK</b> to get started.</div>
            </div>
          ) : (
            <div className="divide-y divide-slate-200" data-testid="stock-list">
              {stock.map((it, i) => (
                <div key={i} className="p-2 flex items-start gap-2 hover:bg-slate-50">
                  <input value={it.name} onChange={e => updateRow(i, "name", e.target.value)}
                    placeholder="Item"
                    className="flex-1 min-w-0 h-8 px-2 border border-slate-300 text-sm font-bold"/>
                  <select value={it.category} onChange={e => updateRow(i, "category", e.target.value)}
                    className="h-8 px-1 border border-slate-300 text-xs font-black uppercase">
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <input type="number" value={it.qty} onChange={e => updateRow(i, "qty", parseInt(e.target.value) || 0)}
                    className="w-16 h-8 px-1 border border-slate-300 text-sm text-right font-mono"/>
                  <select value={it.unit} onChange={e => updateRow(i, "unit", e.target.value)}
                    className="h-8 px-1 border border-slate-300 text-xs">
                    {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                  <button onClick={() => deleteRow(i)}
                    className="w-8 h-8 border border-red-300 text-red-600 hover:bg-red-50 flex items-center justify-center">
                    <Trash weight="bold" size={12}/>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminShell>
  );
}
